import {
  createContext,
  useContext,
  createSignal,
  JSX,
  onMount,
} from "solid-js";
import { useProfile } from "./ProfileContext";

export interface User {
  id: string;
  name: string;
  email: string;
  role: "admin" | "user" | "template_admin";
  /** For template_admin users, restricts them to a specific profile */
  assignedProfile?: "colorado" | "california-aps";
}

interface AuthContextType {
  user: () => User | null;
  login: (email: string, password: string) => Promise<boolean>;
  logout: () => void;
  isAuthenticated: () => boolean;
  isLoading: () => boolean;
}

const AuthContext = createContext<AuthContextType>();

const DUMMY_USERS: User[] = [
  {
    id: "1",
    name: "Case Worker User1",
    email: "admin@coachp.com",
    role: "admin",
  },
  {
    id: "2",
    name: "Case worker User2",
    email: "user@coachp.com",
    role: "user",
  },
  {
    id: "3",
    name: "Demo User",
    email: "demo@coachp.com",
    role: "user",
  },
  {
    id: "4",
    name: "Child Welfare PM",
    email: "pm-cw@coachp.com",
    role: "template_admin",
    assignedProfile: "colorado",
  },
  {
    id: "5",
    name: "APS PM",
    email: "pm-aps@coachp.com",
    role: "template_admin",
    assignedProfile: "california-aps",
  },
];

const DUMMY_CREDENTIALS = [
  { email: "admin@coachp.com", password: "admin123" },
  { email: "user@coachp.com", password: "user123" },
  { email: "demo@coachp.com", password: "demo123" },
  { email: "pm-cw@coachp.com", password: "pm123" },
  { email: "pm-aps@coachp.com", password: "pm123" },
];

export function AuthProvider(props: { children: JSX.Element }) {
  const [user, setUser] = createSignal<User | null>(null);
  const [isLoading, setIsLoading] = createSignal(true);
  const { clearProfile } = useProfile();

  onMount(() => {
    const savedUser = localStorage.getItem("coachp_user");
    if (savedUser) {
      try {
        const userData = JSON.parse(savedUser);
        setUser(userData);
      } catch (error) {
        console.error("Failed to parse saved user data:", error);
        localStorage.removeItem("coachp_user");
      }
    }
    setIsLoading(false);
  });

  const login = async (email: string, password: string): Promise<boolean> => {
    setIsLoading(true);

    try {
      // Simulate network delay
      await new Promise((resolve) => setTimeout(resolve, 1000));

      // Check credentials
      const credential = DUMMY_CREDENTIALS.find(
        (cred) => cred.email === email && cred.password === password,
      );

      if (credential) {
        const userData = DUMMY_USERS.find((u) => u.email === email);
        if (userData) {
          setUser(userData);
          localStorage.setItem("coachp_user", JSON.stringify(userData));
          return true;
        }
      }

      return false;
    } catch (error) {
      console.error("Login error:", error);
      return false;
    } finally {
      setIsLoading(false);
    }
  };

  const logout = (options: { redirect?: boolean } = {}) => {
    const { redirect = true } = options;
    setUser(null);
    localStorage.removeItem("coachp_user");
    clearProfile();
    if (redirect && typeof window !== "undefined") {
      window.location.replace("/");
    }
  };

  const isAuthenticated = () => user() !== null;

  const contextValue: AuthContextType = {
    user,
    login,
    logout,
    isAuthenticated,
    isLoading,
  };

  return (
    <AuthContext.Provider value={contextValue}>
      {props.children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
}
